import type { Event, Graph, WithContext } from 'schema-dts'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'

const useSearchEngineEventSchema = (events: SearchEngineEventItem[]): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const fullLocalizedUrl = useFullLocalizedUrl()

  // Search hits carry unix seconds.
  const formatDate = (seconds: number | undefined) =>
    seconds ? formatSchemaDate(seconds * 1000) : undefined

  const createSchema = (): Event[] => {
    // Events without an upcoming date get no markup: Google treats invented
    // dates as misleading structured data.
    return events
      .filter((event) =>
        searchEngineEventGetters.fnGetNextDateAvailableStart(event)
      )
      .map((event) => {
        const url = fullLocalizedUrl(
          `/e/${searchEngineEventGetters.fnGetPermalink(event)}/`
        )

        const supplierUrl =
          searchEngineEventGetters.getSupplierPermalink(event) &&
          fullLocalizedUrl(
            `/p/${searchEngineEventGetters.getSupplierPermalink(event)}/`
          )

        const endDate = formatDate(
          searchEngineEventGetters.fnGetNextDateAvailableEnd(event)
        )

        const obj: WithContext<Event> = {
          '@type': 'Event',
          name: searchEngineEventGetters.fnGetTitle(event),
          url,
          startDate: formatDate(
            searchEngineEventGetters.fnGetNextDateAvailableStart(event)
          ),
          ...(endDate && { endDate }),
          eventAttendanceMode:
            schemaEventAttendanceModes[
              searchEngineEventGetters.fnGetType(event)
            ],
          // TODO dynamic eventStatus
          eventStatus: 'https://schema.org/EventScheduled',
          // 1200px wide, as Google recommends; width only, see getSchemaImages.
          image: [
            toKonfettiWidth(
              searchEngineEventGetters.getPrincipalImage(event),
              1200
            ),
          ],
          description: searchEngineEventGetters.fnGetSubtitle(event),
          offers: {
            '@type': 'Offer',
            // Hits only carry ticket totals across all dates, not for the
            // next date, so there's nothing reliable to derive SoldOut from.
            availability: 'https://schema.org/InStock',
            price:
              searchEngineEventGetters.getSchemaMinimumPriceAmount(event) / 100,
            url,
            priceCurrency:
              searchEngineEventGetters.fnGetMinimumPriceCurrency(event),
          },
          performer: {
            '@type': 'PerformingGroup',
            name: searchEngineEventGetters.fnGetSupplier(event),
            url: supplierUrl,
          },
          organizer: {
            '@type': 'Organization',
            name: searchEngineEventGetters.fnGetSupplier(event),
            url: supplierUrl,
          },

          // Only konfetti's own event reviews (see getIsEventRating).
          ...(searchEngineEventGetters.getIsEventRating(event) && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: searchEngineEventGetters.getAverageRating(
                event,
                'en-US'
              ),
              bestRating: 5,
              worstRating: 1,
              ratingCount: searchEngineEventGetters.getRatingsAmount(event),
            },
          }),
        }

        if (searchEngineEventGetters.fnGetType(event) !== 'ONLINE') {
          const geo = getSchemaGeo(event._geoloc?.lat, event._geoloc?.lng)

          obj.location = {
            '@type': 'Place',
            name: searchEngineEventGetters.fnGetSupplier(event),
            address: getSchemaPostalAddress({
              formatted: searchEngineEventGetters.fnGetFormattedAddress(event),
              zipcode: searchEngineEventGetters.fnGetFormattedZipcode(event),
              locality: searchEngineEventGetters.getCityName(event),
            }),
            ...(geo && { geo }),
          }
        } else {
          obj.location = {
            '@type': 'VirtualLocation',
            url,
          }
        }

        return obj as WithContext<Event>
      })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useSearchEngineEventSchema
